import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Box, Portal, Typography } from "@mui/material";
import { useMenuStore } from "../../../store/menuState";
import "react-lazy-load-image-component/src/effects/blur.css";
import { ICart, IDetail, IModifier, IModifierGroup, IOrderModifiers } from "../../../types/menu";
import { isBrowser } from 'react-device-detect';
import { useInfoStore } from "../../../store/userInfo";
import { useTranslation } from "react-i18next";
import { logging } from "../../../services/setting.service";
import { useWindowDimensions } from "../../../App";
import './itemDetail.css';
import LandscapeDetail from "./LandscapeDetail";
import PortraitDetail from "./PortraitDetail";
import { ItemDetailContext } from "./ItemDetailContext";
import { useOrderStore } from "../../../store/order";
import { loadMultipleImages } from "../../../utils/imageCache";
import { logger } from "../../../utils/logger";
          

// 플랫폼 확인 Android
const isAndroid = /Android/i.test(navigator.userAgent);

const IMAGE_URL = process.env.REACT_APP_S3_IMAGE_PAPH;

const DynamicDetail = React.memo(() => {
  const { orientation } = useWindowDimensions();
  return orientation === "landscape" ? <LandscapeDetail /> : <PortraitDetail />;
});

  const ItemDetailMain = () => {
    const { userInfo, tableInfo, isAyce } = useInfoStore();
    const storeId = userInfo?.storeId;
    const { height } = useWindowDimensions();
    const { checkUseHour, setSelectedItem, setItemDetail, fetchItemDetail } = useMenuStore()
    const { checkedModifierPrice , setSnackMode, setShowSnackbar, totalGuest, setLimitValue, setOpenCart } = useOrderStore();
    const [open, setOpen] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    // const [quantity, setQuantity] = useState(1)
    const [selectRequired, setSelectRequired] = useState<boolean>(false);
    // const [selectOptionals, setSelectOptionals] = useState<JSON[]>([]);
    const itemDetail = useMenuStore((state) => state.itemDetail);
    const selectedItem = useMenuStore((state) => state.selectedItem)

    // 테블릿/모바일 최적화: 즉시 UI 표시
    const shouldShowImmediately = useMemo(() => {
      return selectedItem && !itemDetail;
    }, [selectedItem, itemDetail]);

    // 즉시 상세 페이지 표시 (테블릿/모바일 최적화)
    useEffect(() => {
      if (selectedItem) {
        setOpen(true);
        // 테블릿에서는 즉시 로딩 상태 표시
        if (!itemDetail) {
          setIsLoading(true);
        }
      }
    }, [selectedItem, itemDetail]);

    // API 호출 최적화 (테블릿/모바일)
    useEffect(() => {
      if(selectedItem && !itemDetail) {
        const startTime = performance.now();
        
        const itemId = selectedItem.itemId;
        const categoryId = selectedItem.categoryId;
        const tableId = tableInfo.id;
        
        // 테블릿/모바일에서는 즉시 API 호출
        fetchItemDetail(itemId, categoryId, tableId)
            .then(() => {
              const endTime = performance.now();
              const loadTime = endTime - startTime;
              logger.log(`📱 Mobile/Tablet: Item detail loaded in ${loadTime.toFixed(2)}ms`);
              setIsLoading(false)
            })
            .catch((error) => {
              const endTime = performance.now();
              const loadTime = endTime - startTime;
              logger.error(`❌ Mobile/Tablet: Item detail failed after ${loadTime.toFixed(2)}ms:`, error);
              setIsLoading(false)
            });
      } else if(selectedItem && itemDetail) {
        setIsLoading(false)
      } else {
        setOpen(false)
        setIsLoading(false)
      }
    },[selectedItem, tableInfo.id, itemDetail, fetchItemDetail])

    const handleClose = useCallback(() => {
      // if (!isBrowser) {
      //   if (window.ReactNativeWebView !== undefined) {
      //     window.ReactNativeWebView.postMessage(JSON.stringify({'type': 'play'}));
      //   } else if(window.Android !== undefined){
      //     window.Android.postMessage(JSON.stringify({'type':'play'}));
      //   }
      // }
      setOpen(false);
      setItemDetail(null)
      setSelectedItem(null)
    }, [setItemDetail, setSelectedItem])

    const handleAddCount = useCallback(async () => {
      if(!itemDetail) return
      // if (!isBrowser) {
      //   if (window.ReactNativeWebView !== undefined) {
      //     window.ReactNativeWebView.postMessage(JSON.stringify({'type': 'play'}));
      //   } else if(window.Android !== undefined){
      //     window.Android.postMessage(JSON.stringify({'type':'play'}));
      //   }
      // }
      if(isAyce) {
        if(itemDetail!.maxAllowedPerTable === 0) {
          setLimitValue(itemDetail!.maxAllowedPerTable)
          setSnackMode(12)
          setShowSnackbar(true)
          return
        }
        if(itemDetail!.maxAllowedPerTable) {
          // setQuantity((prev: number) => (prev >= itemDetail!.maxAllowedPerTable) ? quantity : prev+1)
          const itemQuantity = itemDetail.quantity >= itemDetail!.maxAllowedPerTable ? itemDetail.quantity : itemDetail.quantity+1
          setItemDetail({...itemDetail, quantity: itemQuantity})
        }
        else 
          setItemDetail({...itemDetail, quantity: itemDetail.quantity +  1})
      } else {
          setItemDetail({...itemDetail, quantity: itemDetail.quantity +  1})
      }
    }, [itemDetail, isAyce, setLimitValue, setSnackMode, setShowSnackbar, setItemDetail])

    const handleSubCount = useCallback(() => {
      if(!itemDetail) return
      // if (!isBrowser) {
      //   if (window.ReactNativeWebView !== undefined) {
      //     window.ReactNativeWebView.postMessage(JSON.stringify({'type': 'play'}));
      //   } else if(window.Android !== undefined){
      //     window.Android.postMessage(JSON.stringify({'type':'play'}));
      //   }
      // }
      const itemQuantity = itemDetail.quantity === itemDetail!.minRequired? itemDetail!.minRequired: itemDetail.quantity-1 <= 0 ?  0: itemDetail.quantity-1
      // setQuantity((prev: number) => prev === itemDetail.minRequired ? itemDetail!.minRequired: prev-1 <= 0? 0: prev-1)
      if(itemDetail!.minRequired && itemQuantity < itemDetail!.minRequired) {
        // toast.info(`This can be down to ${itemDetail!.minRequired} items.`)
        setLimitValue(itemDetail!.minRequired)
        setSnackMode(13)
        setShowSnackbar(true)
        return
      }
      setItemDetail({...itemDetail, quantity: itemQuantity})
    }, [itemDetail, setLimitValue, setSnackMode, setShowSnackbar, setItemDetail])

    const getCheckedModifiers = useCallback((groups: IModifierGroup[]): IOrderModifiers[] => {
        return groups.flatMap((group: IModifierGroup) => {
          // Calculate the cumulative quantity for the current group
          const modifierGroupCount = group.modifiers.reduce(
            (sum: number, modifier: IModifier) => sum + (modifier.quantity || 0),
            0
          );
      
          return group.modifiers
            .filter((modifier: IModifier) => modifier.quantity !== null && modifier.quantity > 0)
            .map((modifier: IModifier) => {
              return {
                modifierGroupId: group.id, // Group ID
                modifierGroupName: group.modifierGroupName, // Group Name
                modifierId: modifier.id, // Modifier ID
                modifierName: modifier.modifierName, // Modifier Name
                modifierPrice: modifier.modifierPrice, // Modifier Price
                quantity: modifier.quantity!, // Modifier's selected quantity
                isRequired: group.isRequired,
                path: group.path,
                maxAllowedGroup: group.maxAllowed,
                minRequiredGroup: group.minRequired,
                modifierGroupCount: modifierGroupCount, // Total quantity selected in this group
                hasNestedModifierGroups: modifier.hasNestedModifierGroups,
                orderModifiers: modifier.modifiers
                  ? getCheckedModifiers(modifier.modifiers as IModifierGroup[])
                  : [],
              };
            });
        });
    }, [])

    const checkLimitCountText = useCallback((): string => {
      // console.log(itemDetail, totalGuest)
      const max = itemDetail?.isMaxPerPerson? itemDetail?.maxAllowed! * totalGuest : itemDetail?.maxAllowed
      const min = itemDetail?.minRequired
      if(max && min && min >1) {
        return `Order ${min} to ${max}`
      } else if(max) {
        return `Order up to ${max}`
      } else if(min && min > 1) {
        return `Order at least ${min}`
      }
      return ''; // 기본 반환값 추가
    }, [itemDetail, totalGuest])

    const handleAddtoCart = useCallback(async (e: any, orderItems: IDetail): Promise<void> => {
      // console.log('addtocart',orderItems)

      // if (!isBrowser) {
      //   if (window.ReactNativeWebView !== undefined) {
      //     window.ReactNativeWebView.postMessage(JSON.stringify({'type': 'play'}));
      //   } else if(window.Android !== undefined){
      //     window.Android.postMessage(JSON.stringify({'type':'play'}));
      //   }
      // }

      // if(!checkUseHour) {
      //   setItemDetail({} as IDetail);
      //   // setSelectOptionals([])
      //   checkedModifierPrice({price: 0, type: 'reset'});
      //   setSelectRequired(false);
      //   // setDetailRequire(false)
      //   // setOrderNotice(true)
      //   setOpen(false);
      //   return
      // }
      
      if (storeId) logging(JSON.stringify('detail addtocart: '+selectRequired ), storeId,tableInfo.id)

      const key =  orderItems.itemId
      // const name = detailItem.itemName;
      const price = orderItems.itemPrice;
      const hasModifier = orderItems.hasModifier;
      const specialRequest =  orderItems.specialRequest
      // dispatch(setAddSnackbar(true));
      if(hasModifier && !selectRequired) {
        // console.log('require false')
        setSnackMode(0)
        setShowSnackbar(true)

        return; 
      }

      // Optional modifier의 min 조건 체크
      if (hasModifier && orderItems?.modifierGroups) {
        const checkOptionalMinRequirements = (groups: any[]): { isValid: boolean; minValue?: number; groupName?: string } => {
          for (const group of groups) {
            if (!group.modifiers) continue;

            // 현재 그룹의 선택된 수량 계산
            const currentGroupQuantity = group.modifiers.reduce(
              (sum: number, mod: any) => sum + (mod.quantity || 0),
              0
            );

            // Optional 그룹이지만 minRequired가 설정된 경우 체크
            // 단, 하나라도 선택된 경우에만 min 조건을 체크
            if (!group.isRequired && group.minRequired !== null && group.minRequired > 0 && currentGroupQuantity > 0) {
              if (currentGroupQuantity < group.minRequired) {
                return { 
                  isValid: false, 
                  minValue: group.minRequired,
                  groupName: group.modifierGroupName 
                };
              }
            }

            // 재귀적으로 하위 modifierGroup 체크
            for (const mod of group.modifiers) {
              if (mod.modifiers && mod.modifiers.length > 0) {
                const result = checkOptionalMinRequirements(mod.modifiers);
                if (!result.isValid) {
                  return result;
                }
              }
            }
          }
          return { isValid: true };
        };

        const optionalCheck = checkOptionalMinRequirements(orderItems.modifierGroups);
        if (!optionalCheck.isValid) {
          // 새로운 snackMode 추가 (15번)
          setSnackMode(15);
          setLimitValue(optionalCheck.minValue || 0);
          setShowSnackbar(true);
          return;
        }
      }

      const orderItem:ICart = {
        categoryId: orderItems.categoryId,
        itemId: key,
        itemName: orderItems.itemName,
        // orderTypeId?: number,
        newItem: true,
        quantity: orderItems.quantity,
        isAyce: isAyce,
        itemPrice: price,
        hasModifier: hasModifier,
        itemWithModifierPrice: 0,
        maxAllowed: orderItems.maxAllowed,
        minRequired:  orderItems.minRequired,
        categoryLimits: orderItems.categoryLimits,
        maxAllowedPerTable: orderItems.maxAllowedPerTable,
        orderModifiers: [],
      }
      if(specialRequest !== undefined) Object.assign(orderItem, {specialRequest: specialRequest})
      if(hasModifier) {
        const checkedModifiers: IOrderModifiers[] = getCheckedModifiers(orderItems.modifierGroups!);
        if (checkedModifiers.length > 0) {
          orderItem.orderModifiers = checkedModifiers;
        } else {
          orderItem.orderModifiers = [];
        }
      }
      // addRecommendsMenu();
              // console.log('data', orderItem)
      // await setCart({item: menu, type: "add"})
      try {
        await useOrderStore.getState().setCart({ item: orderItem, type: 'add' });
        // console.log('Cart updated successfully');
        // 추가 작업 수행
        // setOpenCart(true)
        const { updateCart } = useOrderStore.getState();
        if (updateCart) {
          // console.log('detail add to cart');
          setSnackMode(3);
          setShowSnackbar(true);
        }
      } catch (error) {
        // console.error('Failed to update cart', error);
        // 오류 처리
      }

      // if(isAyce) {
      //   if(isItemMaxAllowed) {
      //     setLimitValue(itemDetail!.maxAllowedPerTable!)
      //     setSnackMode(12)
      //     setShowSnackbar(true)
      //   } 
      //   if(isCategoryMaxAllowed) {
      //     setLimitValue(itemDetail!.categoryLimits.maxAllowed!)
      //     setSnackMode(10)
      //     setShowSnackbar(true)
      //   }
      // }

      // setSelectOptionals([])
      checkedModifierPrice({price: 0, type: 'reset'});
      setSelectRequired(false);

      setOpen(false);
      setSelectedItem(null)
      setItemDetail(null)

      // setIsAddCart(false)
      // setIsAddCart(true)
      // dispatch(setDetailRequire(false))
    }, [storeId, selectRequired, tableInfo.id, setSnackMode, setShowSnackbar, isAyce, getCheckedModifiers, checkedModifierPrice, setSelectRequired, setOpen, setSelectedItem, setItemDetail])

    // selectedItem이 있으면 상세 페이지 표시 (로딩 중이어도)
    if(!selectedItem) {
      return null;
    }



    // itemDetail이 없으면 로딩 중이므로 상세 페이지는 표시하지 않음
    if(!itemDetail) {
      return null;
    }
  
    
    return (
      <ItemDetailContext.Provider value={{ itemDetail, selectRequired, setSelectRequired, setItemDetail, handleClose, handleAddCount, handleSubCount, handleAddtoCart, checkLimitCountText }}>
        <Portal>
          <Box className={`dialog ${open ? 'show' : ''}`} onClick={handleClose} >
            <DynamicDetail />
          </Box>
        </Portal>
      </ItemDetailContext.Provider>
    )
  }
 
export default ItemDetailMain


